import { z } from 'zod';
import { AuditActorSchema } from './audit';
import { type FindingState, SeveritySchema } from './enums';
import {
  AssessmentIdSchema,
  DecisionBatchIdSchema,
  DecisionIdSchema,
  FindingIdSchema,
  PackIdSchema,
  TenantIdSchema,
} from './ids';
import { refuseLegalConclusion } from './control-catalogue';
import type { PersistedFinding } from './finding';
import { NonEmptyStringSchema, SemVerSchema, TimestampSchema } from './primitives';
import { SLOT_REVIEWER_EDITABLE, SlotKindSchema, withAuthoredSlots } from './statement';

/**
 * The human gate: what a reviewer decides, and what the record of it holds
 * (docs/DECISIONS.md ZD-061, CLAUDE.md principle 3, DESIGN_SPEC §11).
 *
 * ## What this object is for, stated because it bounds what it may claim
 *
 * Everything upstream of here is a machine reading documents. This is the point
 * where a person takes responsibility for what a client is told — and it is
 * worth being exact about how much that is, because the schema must not imply
 * more than the gate can deliver (ZD-061 §4).
 *
 * A reviewer, reading what the report shows them, can catch a finding on the
 * wrong object, a threat class that does not apply to this client, a control the
 * client holds that the model did not see, and a band that reads wrong for the
 * business. They **cannot** catch a missing finding: the report enumerates what
 * fired and nothing in it enumerates what did not, so the gate does not touch
 * CLAUDE.md principle 9's primary failure mode at all. They cannot catch a wrong
 * reference resolution or a missed merge, which are indistinguishable from the
 * right ones by construction (ZD-047). And on **50 of the corpus's 102
 * findings** there is no citation to check — a conditional finding rests on an
 * `unknown` verdict, which carries no witnesses, so it cites nothing.
 *
 * So an acceptance means *this may be shown to the client*, and not *this
 * finding is correct*. The gate contains false positives a competent reader can
 * recognise and establishes that a named person took responsibility on a dated
 * record. It is not a verification of grounding — `validateFinding` does that,
 * in code, before a human sees anything.
 *
 * ## Two axes, not four states
 *
 * `approved`, `edited`, `rejected` and `accepted_risk` read as four coordinate
 * decisions and are not. **Is this true of the client's system?** is
 * `disposition`. **What is done about it?** is `treatment`, and it presupposes
 * the first answered yes — filing `accepted_risk` as a sibling of `rejected`
 * leaves a register unable to tell *"not true of us"* from *"true, and we accept
 * it"*, which have opposite consequences for the report and for a re-run. And
 * `edited` is not a decision at all but a modification accompanying one, so as a
 * peer state it loses which way the reviewer decided.
 *
 * `FindingState` is therefore **derived** from a decision by `findingStateFor`,
 * the move ZD-043 §6 made for `prose_origin` and ZD-058 for confidence.
 */

/** Is this true of the client's system? */
export const DISPOSITIONS = ['accept', 'reject', 'defer'] as const;
export const DispositionSchema = z.enum(DISPOSITIONS);
export type Disposition = z.infer<typeof DispositionSchema>;

/** Given that it is true, what happens about it? Only meaningful on an accept. */
export const TREATMENTS = ['remediate', 'accepted_risk'] as const;
export const TreatmentSchema = z.enum(TREATMENTS);
export type Treatment = z.infer<typeof TreatmentSchema>;

export class SlotNotEditableError extends Error {
  constructor(slot: string) {
    super(
      `slot ${slot} may not be edited: it carries either a fact about the client's architecture ` +
        'or text that must survive verbatim',
    );
    this.name = 'SlotNotEditableError';
  }
}

/**
 * One sentence a reviewer rewrote.
 *
 * **A reviewer may edit almost exactly what a model may write, and the one row
 * where they differ is the interesting one** (ZD-061 §2, amended by ZD-063 §2).
 * `SLOT_REVIEWER_EDITABLE`'s refusals have two reasons and both hold against a
 * person:
 *
 *  - *The slot carries a claim about the architecture* — `subject`,
 *    `impacted_assets`, `crossed_boundary`, `observed_controls`, `undecided`. A
 *    model may not write these because ZD-031 keeps architecture claims in code.
 *    A reviewer may not either, and for a **stronger** reason: the slot is a
 *    transcript of the canonical model, and everything downstream — scoring's
 *    `impacted_assets`, the reproducibility tuple's `grounded_object_ids`,
 *    `evidence_refs` — still reads the model. An edit there is a second source
 *    of truth, which is ZD-017's exact refusal. If the reviewer believes the
 *    model is wrong, the repair is upstream: a clarification answer, or a
 *    re-run.
 *  - *The text must survive verbatim* — `control_direction` carries catalogue
 *    sentences with an efficacy caveat that must not be paraphrased softer, and
 *    `assertVerbatim` refuses it regardless of who typed.
 *
 * So the editable set is `threat_action` and `grounds`, **unchanged** — this
 * entry's own conclusion still holds for a person. What ZD-063 §2 changed is
 * the sentence beside it: *what differs between a model and a person is the
 * origin marker and the audit, not the permission* was true while no third
 * reason existed, and a third one arrived with the contextualiser. It bears on
 * `threat_action` alone, applies to a model alone, and is measured rather than
 * argued — see `SLOT_MODEL_AUTHORABLE`.
 *
 * Note what editing `threat_action` means, and it is why a person keeps it: it
 * is the pattern's authored text carried verbatim, so a reviewer editing it is
 * contextualising QRS pack content for one client — legitimate consultancy, and
 * exactly why it has to be visible as `approver_edited` rather than blending
 * into `rendered`.
 */
export const SlotEditSchema = z
  .object({
    slot: SlotKindSchema,
    /**
     * What the reviewer wrote. Laid beside the transcript as
     * `Slot.authored_text` by `findingUnderDecision` — at read time, never on
     * the stored finding (docs/DECISIONS.md ZD-125 §1). Until `sds-12o` this
     * line said the text *"lands in `Slot.authored_text`"* and nothing anywhere
     * wrote it there: `withAuthoredSlots` had no production caller, so an edit
     * was stored, audited, made `findingStateFor` answer `edited`, and reached
     * no reader.
     */
    text: NonEmptyStringSchema,
  })
  .superRefine((edit, ctx) => {
    if (!SLOT_REVIEWER_EDITABLE[edit.slot]) {
      ctx.addIssue({
        code: 'custom',
        message: new SlotNotEditableError(edit.slot).message,
        path: ['slot'],
      });
    }
    /*
     * **The language guard for a reviewer's sentence is this parse, and it is
     * the only one** (ZD-125 §1). A reviewer's edit is the one prose path in
     * the product no guard saw before a client: `runQaReview` walks
     * `authored_text` at `qa_review_complete`, one state *before* the gate
     * where an edit is written, and `refuseConclusions` runs at
     * `report_generated`, one transition *after* the person signed it — ZD-080
     * §3's defect, on the one path that is not a model's. Refusing here is
     * refusing where the person acts: at `/decisions`, in the record, and in a
     * console pre-flighting with this same schema, so the sentence a reviewer
     * reads is the API's own.
     *
     * Deliberately **not** also walked by `refuseConclusions` when the report
     * prints the sentence: an edit that reached a report has already passed
     * this parse, so that walk could never fire and its mutation would report
     * STAYED GREEN while reading as a protection (ZD-048 §7).
     */
    const phrases = refuseLegalConclusion(edit.text);
    if (phrases.length > 0) {
      ctx.addIssue({
        code: 'custom',
        message:
          `the edit states a legal conclusion about the organisation (${phrases.join(', ')}); ` +
          'a finding says what the design evidences, never whether the client complies',
        path: ['text'],
      });
    }
    /*
     * One paragraph. The report prints the sentence as one line under the
     * finding it belongs to, and a line break inside it would put the rest
     * outside that line — the shape `UnrenderableControlTextError` refuses for
     * catalogue text, at the point of writing rather than of rendering.
     */
    if (/[\r\n]/u.test(edit.text)) {
      ctx.addIssue({
        code: 'custom',
        message: 'write the edit as one paragraph; it is printed as one line under the finding',
        path: ['text'],
      });
    }
  });
export type SlotEdit = z.infer<typeof SlotEditSchema>;

/**
 * A reviewer's stated disagreement with the derived severity band.
 *
 * **Recorded beside the band and never replacing it** (ZD-061 §5). A consultant
 * genuinely does form a different view, and suppressing that would lose real
 * judgement; overwriting the band would be worse. Two reasons, and the first is
 * measured: ZD-058 §1 refused *"keep the multiplier and emit an unadjusted band
 * beside it"* on the ground that two bands on one finding is the thing a client
 * quotes wrongly — a replaced band creates the same pair, one step later and
 * with no record that it moved. And a band replaced by hand is a second scoring
 * method with one row of input, in a field whose whole claim is that it is
 * derived.
 *
 * **It is not a confidence field wearing a different name, and the pressure to
 * make it one is real**: 39 of the 42 findings this method bands `critical` or
 * `high` carry *low* derived confidence (ZD-058 §1), so a reviewer will want to
 * say *"no, I am sure about this one"*. There is nowhere here to say it, on
 * CLAUDE.md principle 5. The honest route is to answer the clarification, which
 * changes the provenance, which changes the confidence.
 */
export const SeverityDissentSchema = z.strictObject({
  band: SeveritySchema,
  reason: NonEmptyStringSchema,
});
export type SeverityDissent = z.infer<typeof SeverityDissentSchema>;

/**
 * **Strict, and the omissions are the point** — the same posture
 * `CandidateFindingDraftSchema` takes, for the same reason.
 *
 * There is no `confidence` and no `severity_normalised`. A non-strict object
 * would *silently drop* either, so a reviewer UI sending one would get a
 * success and no effect, which is worse than a refusal because nothing tells
 * anybody the field did nothing. That is ZD-058 §5's finding one object along:
 * `default_scoring_inputs` admitted `confidence` and `control_strength` because
 * it was `.partial()` rather than strict, and closing it **by construction**
 * beat adding a refinement.
 */
const DecisionShape = z.strictObject({
  decision_id: DecisionIdSchema,
  tenant_id: TenantIdSchema,
  assessment_id: AssessmentIdSchema,

  /**
   * **The anchor, and it is not `finding_id`** (ZD-061 §3).
   *
   * `reproducibilityKey()` — domain, threat pattern and sorted grounded object
   * ids. `finding_id` is minted by `newId` and is random per run, so a decision
   * keyed on it survives no re-analysis, no clarification round and no pack
   * revision. This is the only identity a finding has that does survive one, and
   * it is stable precisely because ZD-028/ZD-049 make a canonical object id a
   * digest of its identity rather than a fresh value.
   */
  finding_key: NonEmptyStringSchema,

  /**
   * `(domain, threat_pattern_id, subject_id)` — the *coarser* identity, and it
   * is not a second copy of the one above (docs/DECISIONS.md ZD-064 §4).
   *
   * `finding_key` contains the whole grounded set and cannot say **which** of
   * those ids was the subject, so the structural key is not recoverable from
   * it. That matters because of what a clarification round does: settling a
   * conditional finding moves the verdict `unknown → true`, and ZD-029 §6
   * derives the grounding from the predicate's own witnesses — so the newly
   * established attribute joins the grounding and `finding_key` changes.
   * **Measured: on 10 of 102 corpus findings**, every one of them a finding a
   * reviewer would have deferred.
   *
   * With only `finding_key`, a decision on such a finding stops resolving and
   * reads as one nobody ever made. With both, the two cases a reviewer needs
   * told apart are decidable: the same pattern still fires on the same object
   * and now says something different (**re-shown, with what it said before**),
   * against the pattern no longer firing there at all (**withdrawn**).
   */
  finding_structural_key: NonEmptyStringSchema,

  /**
   * The run-local finding the reviewer was actually looking at.
   *
   * Carried as well as the key, and not instead of it: the audit trail has to
   * name the object a person had in front of them, and after a re-run that
   * object no longer exists. The key is what the decision *follows*; this is
   * what it was *made on*.
   */
  finding_id: FindingIdSchema,

  /**
   * The pack the pattern came from when the decision was made.
   *
   * A decision is a judgement about what a *pattern* says, so a pattern that has
   * since been re-authored is a different claim and the decision is stale
   * (ZD-061 §3). Coarse on purpose: the pack version is the only content anchor
   * available, and a per-pattern content digest — which would make this precise
   * rather than conservative — is a pack schema change this session did not
   * make. Being coarse fails safe: it re-shows a decision that may not have
   * needed re-making, rather than reusing one that did.
   */
  pack_id: PackIdSchema,
  pack_version: SemVerSchema,

  disposition: DispositionSchema,
  treatment: TreatmentSchema.nullable(),

  /** Why. Required on `reject` and `defer` — see the refinement below. */
  reason: NonEmptyStringSchema.nullable(),

  edits: z.array(SlotEditSchema),
  severity_dissent: SeverityDissentSchema.nullable(),

  /**
   * Who decided. **Kind `user`, and the check is here as well as on the
   * transition guard** (ZD-007).
   *
   * Not the masking twin ZD-048 §7 warns about, and the reason is that the two
   * reach different code. The guard judges the actor of the *transition*, which
   * `state-machine.test.ts` drives directly against constructed facts without
   * ever building a decision; this judges the actor recorded *inside* a
   * decision. Breaking either one alone reddens its own tests, which is the
   * property that separates two rules from two copies of one.
   */
  decided_by: AuditActorSchema,
  decided_at: TimestampSchema,

  /**
   * The decision this one replaces, where a reviewer changed their mind.
   *
   * A changed mind is a **new record**, never an edit: before this, decisions
   * were a `Record<finding_id, FindingState>` merged into the pipeline facts, so
   * re-deciding overwrote the earlier answer and the audit event said only how
   * many decisions were in the call. Who moved which finding, when, and away
   * from what was unrecoverable — in the one artefact an auditor asks for.
   */
  supersedes: DecisionIdSchema.nullable(),

  /**
   * The group this decision was recorded in, or `null` for a decision made on
   * one finding by itself (docs/DECISIONS.md ZD-127 §1).
   *
   * **Stamped by `recordDecisions`, never read from the body**, on
   * `decided_at`'s precedent; the `DecisionBatch` record it names holds the
   * group's reason and members. This is the field that lets the record answer
   * *was this finding considered individually?* — before it, thirty-five
   * decisions at one millisecond implied a group without declaring one, and a
   * coincidence is not a claim (ZD-035 §2).
   *
   * **Defaults to `null` on a decision that has no key at all**, and that is
   * the truthful value rather than a migration convenience: a decision
   * recorded before this field existed declared no batch, whatever its
   * timestamp shares with its neighbours. The record cannot be rewritten to
   * make a claim nobody made, so the live review's thirty-five stay singular
   * in the record and the report says nothing about a group over them.
   */
  batch_id: DecisionBatchIdSchema.nullable().default(null),
});

/**
 * The dispositions a batch may carry, with the one treatment an accept may
 * take in one (ZD-127 §2). Read by the console rather than restated there, so
 * the batch bar offers exactly what the schema records.
 *
 * `defer` is not here because a deferral names what it waits on, per finding;
 * `accepted_risk` is not here because accepting a risk is a judgement about one
 * risk. Both are refused in a batch by the refinement below, with the field
 * named, so a bulk action can never be the route by which a per-finding
 * judgement is made about a set.
 */
export const BATCHABLE_DISPOSITIONS = ['accept', 'reject'] as const;
export type BatchableDisposition = (typeof BATCHABLE_DISPOSITIONS)[number];
export const BATCH_TREATMENT: Treatment = 'remediate';

export const ApproverDecisionSchema = DecisionShape.superRefine((decision, ctx) => {
  if (decision.decided_by.kind !== 'user') {
    ctx.addIssue({
      code: 'custom',
      message:
        `a finding decision must be made by an actor of kind "user"; got ` +
        `"${decision.decided_by.kind}". No worker or orchestrator may decide a finding.`,
      path: ['decided_by', 'kind'],
    });
  }

  /*
   * A rejection or a deferral without a reason is the record failing at the one
   * thing it exists for. A rejected finding is retained rather than deleted so
   * that a pack revision can re-evaluate what was refused and why; a reason-less
   * rejection retains the refusal and loses the why, which makes the retention
   * decorative. A deferral without one cannot tell a reader what it is waiting
   * for.
   *
   * Not mirrored in a transition guard, deliberately: `core.recordDecisions`
   * parses through this schema, so no reason-less decision can reach the facts a
   * guard reads, and a guard clause for it would be one that cannot fire —
   * the redundant twin that makes a mutation report STAYED GREEN (ZD-048 §7,
   * ZD-027 rule 3).
   */
  /*
   * **Unless the decision is a member of a batch, whose reason it is** (ZD-127
   * §2). Copying a group's sentence onto thirty-five rows would manufacture
   * thirty-five independent reasons — the record would then say thirty-five
   * judgements were made where one was — so a batched reject may carry no
   * reason of its own, and `DecisionBatchRequestSchema` is what requires the
   * batch to state one. A batched reject may still add a reason; it need not.
   */
  if (decision.disposition !== 'accept' && decision.reason === null && decision.batch_id === null) {
    ctx.addIssue({
      code: 'custom',
      message: `a decision to ${decision.disposition} must state a reason`,
      path: ['reason'],
    });
  }

  /*
   * What a batch may not carry, refused on the member and naming the field
   * (ZD-127 §2). Each of these is a judgement about one finding: a deferral
   * names what *this* finding waits on, an accepted risk is a decision about
   * one risk, a rewrite is a sentence about one finding and a dissent is about
   * one band. A bulk action can never be the route by which one of them is
   * made about a set. These live here, on the record, and not also on the
   * request schema: a rule in both places is the redundant twin whose mutation
   * reports STAYED GREEN (ZD-048 §7).
   */
  if (decision.batch_id !== null) {
    if (decision.disposition === 'defer') {
      ctx.addIssue({
        code: 'custom',
        message:
          'a deferral names what one finding is waiting on, so it cannot be made in a batch; ' +
          'defer the finding by itself',
        path: ['disposition'],
      });
    }
    if (decision.treatment === 'accepted_risk') {
      ctx.addIssue({
        code: 'custom',
        message:
          'accepting a risk is a judgement about one risk, so it cannot be made in a batch; ' +
          'record it on the finding by itself',
        path: ['treatment'],
      });
    }
    if (decision.edits.length > 0) {
      ctx.addIssue({
        code: 'custom',
        message: 'a rewrite is a sentence about one finding, so it cannot be recorded in a batch',
        path: ['edits'],
      });
    }
    if (decision.severity_dissent !== null) {
      ctx.addIssue({
        code: 'custom',
        message:
          'a severity dissent is about one finding’s band, so it cannot be recorded in a batch',
        path: ['severity_dissent'],
      });
    }
  }

  /*
   * `treatment` answers "what happens about it", which only arises once
   * "is it true" has been answered yes. A rejected finding with a treatment is
   * two contradictory claims in one record.
   */
  if (decision.disposition !== 'accept' && decision.treatment !== null) {
    ctx.addIssue({
      code: 'custom',
      message: `only an accepted finding carries a treatment; this one is a ${decision.disposition}`,
      path: ['treatment'],
    });
  }
  if (decision.disposition === 'accept' && decision.treatment === null) {
    ctx.addIssue({
      code: 'custom',
      message: 'an accepted finding must say what happens about it',
      path: ['treatment'],
    });
  }

  /*
   * Dissent is a disagreement with the band an accepted finding carries. On a
   * rejection there is no band being asserted to disagree with, and on a
   * deferral the finding is not yet being asserted at all.
   */
  if (decision.disposition !== 'accept' && decision.severity_dissent !== null) {
    ctx.addIssue({
      code: 'custom',
      message: 'a severity dissent belongs to an accepted finding, whose band is being asserted',
      path: ['severity_dissent'],
    });
  }

  /*
   * An edit to a rejected finding is text with no reader: a rejected finding
   * never reaches a client. A deferred one may come back, so a reviewer who
   * corrects the wording and then asks the client keeps their correction.
   */
  if (decision.disposition === 'reject' && decision.edits.length > 0) {
    ctx.addIssue({
      code: 'custom',
      message: 'a rejected finding is never shown to a client, so an edit to it has no reader',
      path: ['edits'],
    });
  }

  const seen = new Set<string>();
  for (const [i, edit] of decision.edits.entries()) {
    if (seen.has(edit.slot)) {
      ctx.addIssue({
        code: 'custom',
        message: `two edits to slot ${edit.slot}; a slot has one sentence`,
        path: ['edits', i, 'slot'],
      });
    }
    seen.add(edit.slot);
  }
});
export type ApproverDecision = z.infer<typeof DecisionShape>;

/**
 * A group of decisions a reviewer recorded as one act, and the record of it
 * (docs/DECISIONS.md ZD-127 §1).
 *
 * ## Why it is a record and not a coincidence
 *
 * `recordDecisions` reads its clock once per call, so every decision in one
 * call already shares `decided_at`. The first live review recorded thirty-five
 * decisions at one millisecond and the record *implied* a group without
 * declaring one — and the report over it said *"a reviewer worked through 36
 * findings"*, a sentence the record could not back. A coincidence is not a
 * claim (ZD-035 §2); this is the claim.
 *
 * ## The reason lives here and nowhere else
 *
 * One `reason`, on the batch, required where the batch rejects. Its members
 * carry `batch_id` and may carry no reason of their own, because thirty-five
 * copies of one sentence would be thirty-five independent judgements in the
 * record where one was made. A reader of any member finds the reason here.
 *
 * ## One verdict
 *
 * A batch is one verdict applied to a selection, so `finding_keys` are the
 * members and the verdict is read off them — every member shares one
 * disposition, refused otherwise by `DecisionBatchRequestSchema`. ZD-126 §7
 * proposed *a reason where any member rejects*, which admitted a batch mixing
 * accepts and rejects under one reason: a reason attached to a set it is not
 * true of. Requiring one disposition is what makes the reason a claim about
 * the whole thing.
 *
 * Stamped fields are stamped by `recordDecisions` on `ApproverDecision`'s
 * precedent: `batch_id`, the actor, the instant and the member keys are the
 * orchestrator's, and a caller supplies only the reason.
 */
export const DecisionBatchSchema = z.strictObject({
  batch_id: DecisionBatchIdSchema,
  tenant_id: TenantIdSchema,
  assessment_id: AssessmentIdSchema,
  /** Why, for the group. Required where the group rejects — see the request schema. */
  reason: NonEmptyStringSchema.nullable(),
  decided_by: AuditActorSchema,
  decided_at: TimestampSchema,
  /** The members, by the key a decision follows across runs (ZD-061 §3). Two or more. */
  finding_keys: z.array(NonEmptyStringSchema).min(2),
});
export type DecisionBatch = z.infer<typeof DecisionBatchSchema>;

/** What a caller declares beside two or more decisions. */
export const BatchDeclarationSchema = z.strictObject({
  reason: NonEmptyStringSchema.nullable(),
});
export type BatchDeclaration = z.infer<typeof BatchDeclarationSchema>;

/**
 * The part of a proposed decision a batch rule reads. Loose on purpose: the
 * members are parsed in full, with their stamped fields, by
 * `ApproverDecisionSchema` afterwards — this schema judges the *set*, and
 * restating the member shape here would be a second copy of `DecisionShape`.
 */
const BatchMemberSchema = z
  .object({
    finding_key: NonEmptyStringSchema,
    disposition: DispositionSchema,
    treatment: TreatmentSchema.nullable(),
  })
  .loose();

/**
 * What a call carrying several decisions must say about them, judged as a set
 * (ZD-127 §2). **Every multi-decision call declares itself a batch or is
 * refused** — `recordDecisions` holds that rule — and this is what a declared
 * one must satisfy. Run in the console before a batch is sent, so the sentence
 * a reviewer reads is this schema's own (ZD-118 §3's convention).
 *
 * The four rules here are the collective ones, and none is repeated on the
 * member: two or more members, because a batch of one is a decision on one
 * finding and is recorded as one; one disposition across the set, in
 * `BATCHABLE_DISPOSITIONS`; a reason where that disposition rejects; and no
 * finding named twice, because a set that decides one finding two ways has
 * decided it neither.
 */
export const DecisionBatchRequestSchema = z
  .object({
    batch: BatchDeclarationSchema,
    decisions: z.array(BatchMemberSchema).min(2, {
      message: 'a batch is two or more decisions; a decision on one finding is recorded by itself',
    }),
  })
  .superRefine((request, ctx) => {
    const dispositions = new Set(request.decisions.map((member) => member.disposition));
    if (dispositions.size > 1) {
      ctx.addIssue({
        code: 'custom',
        message:
          'a batch is one verdict applied to every finding in it; these decisions carry ' +
          `${[...dispositions].sort().join(' and ')}, so its reason would be a claim about a set ` +
          'it is not true of',
        path: ['decisions'],
      });
    }
    const [disposition] = dispositions;
    if (
      dispositions.size === 1 &&
      disposition !== undefined &&
      !(BATCHABLE_DISPOSITIONS as readonly string[]).includes(disposition)
    ) {
      ctx.addIssue({
        code: 'custom',
        message: `a batch may accept or reject; ${disposition} is a judgement about one finding`,
        path: ['decisions'],
      });
    }
    if (dispositions.size === 1 && disposition === 'reject' && request.batch.reason === null) {
      ctx.addIssue({
        code: 'custom',
        message:
          'a batch that rejects must state why, once, for the group: every rejected finding ' +
          'is kept rather than deleted so that what was refused and why can be re-read',
        path: ['batch', 'reason'],
      });
    }
    const seen = new Set<string>();
    for (const [i, member] of request.decisions.entries()) {
      if (seen.has(member.finding_key)) {
        ctx.addIssue({
          code: 'custom',
          message: 'a finding is named twice in this batch; a batch decides each finding once',
          path: ['decisions', i, 'finding_key'],
        });
      }
      seen.add(member.finding_key);
    }
  });
export type DecisionBatchRequest = z.infer<typeof DecisionBatchRequestSchema>;

/**
 * The finding state a decision produces (ZD-061 §1).
 *
 * Derived rather than asserted, which is what makes `edited` a **consequence**
 * of an accept that carried an edit rather than a fifth thing a reviewer could
 * choose instead of accepting.
 */
export function findingStateFor(decision: ApproverDecision): FindingState {
  if (decision.disposition === 'defer') return 'deferred';
  if (decision.disposition === 'reject') return 'rejected';
  if (decision.treatment === 'accepted_risk') return 'accepted_risk';
  return decision.edits.length > 0 ? 'edited' : 'approved';
}

/**
 * Whether a decision releases its finding to a client at report time.
 *
 * **`accepted_risk` releases, and that is the split of axes arriving as
 * behaviour.** An accepted-risk finding is one the reviewer judged *true* and
 * chose not to remediate; a risk register that omits the risks a client has
 * accepted is a false picture of their system, and the acceptance is what
 * `Action.status` carries onward (DESIGN_SPEC §7.8). Under the old flat
 * vocabulary it read as a sibling of `rejected` and was dropped with it — which
 * is the concrete cost of collapsing "is it true" and "what do we do about it"
 * into one field.
 */
export const releasesToClient = (decision: ApproverDecision): boolean =>
  decision.disposition === 'accept';

/**
 * A finding as its standing decision has it: the reviewer's sentences laid
 * beside the transcript (docs/DECISIONS.md ZD-125 §1).
 *
 * ## Resolved at read time, and the reasons are structural
 *
 * A stored finding is the analysis run's own record — one R2 payload per run,
 * with `findings.sha256` written at store time — and a decision is a separate
 * record that supersedes by being newer (ZD-102 §3). Writing an edit *onto*
 * the finding would make the run's output mutable after the fact, and would
 * leave the previous reviewer's sentence on the record after a superseding
 * decision that carried none: a cache with no invalidation, which ZD-065
 * refuses by asking *could our own code compute it, and does nothing compare
 * the two*. Here the answer is yes and nothing, so nothing stores it.
 *
 * ## One function, two readers
 *
 * `GET /findings` calls this so a console shows the finding as decided, and
 * `generateReportForAssessment` calls it so the report prints what the
 * reviewer wrote. Each is a read that presents a finding under its decision;
 * a third such read calls this rather than choosing for itself, because two
 * callers choosing would be two answers to *what does this finding say*
 * (`renderStatement`'s own rule, one layer down).
 *
 * The transcript survives beside the sentence — `withAuthoredSlots` writes
 * `authored_text` and never `text` — so *what did the pipeline actually say*
 * is still answerable from the returned object (ZD-061 §2), and
 * `statementProseOrigin` reads `approver_edited` off it rather than being
 * told.
 *
 * A decision with no edits returns the entry untouched, by identity. A
 * rejection can carry none (`ApproverDecisionSchema` refuses one), so the
 * released set and the edited set are disjoint by construction.
 */
export function findingUnderDecision(
  entry: PersistedFinding,
  decision: ApproverDecision | undefined,
): PersistedFinding {
  if (decision === undefined || decision.edits.length === 0) return entry;
  return {
    ...entry,
    statement: withAuthoredSlots(entry.statement, decision.edits, 'approver_edited'),
  };
}
